import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BlahService } from '@headpower/blah-ng';
import { ToastDuration, ToasterService } from '@headpower/layout';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { debounceTime, finalize, map, startWith, takeUntil, timeInterval } from 'rxjs/operators';
import { IwmLocale, IwmLocaleService } from 'src/app/services/iwm-locale.service';
import { AddItemsToTenderingService, TenderingAsListItem } from './add-items-to-tendering.service';
import { TimeoutIntervalService } from 'src/app/services/timeout-interval.service';

export class AddItemsToTenderingDialogData {
  sourceType: 'Task' | 'Worksite';
  sourceId: string;
  sourceIwmId: number;
  mobile: boolean;
  allowedToCopyUnits: boolean;
}

@Component({
  selector: 'app-add-items-to-tendering',
  templateUrl: './add-items-to-tendering-dialog.component.html',
  styleUrls: ['./add-items-to-tendering-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddItemsToTenderingDialogComponent implements OnInit, OnDestroy {

  public mobile: boolean = false;
  public tenderings: Array<TenderingAsListItem> = [];

  public copyFileAttachments: UntypedFormControl = new UntypedFormControl(true);
  public copyUnits: UntypedFormControl = new UntypedFormControl(true);
  public tenderingControl = new UntypedFormControl();
  public filteredOptions: Observable<TenderingAsListItem[]>;

  public sourceType: 'Task' | 'Worksite';
  public sourceId: string;
  public source: string;

  public allowedToCopyUnits: boolean = false;
  private locale: IwmLocale;

  public addInProgress: boolean = false;
  private timeoutInterval: number;  // milliseconds 

  private destroy$: Subject<void> = new Subject();

  constructor(private dialogRef: MatDialogRef<AddItemsToTenderingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: AddItemsToTenderingDialogData,
    private tenderingService: AddItemsToTenderingService,
    private cdRef: ChangeDetectorRef,
    private blahService: BlahService,
    private toaster: ToasterService,
    private timeoutIntervalService: TimeoutIntervalService,
    private localeService: IwmLocaleService) {
    this.mobile = this.dialogData.mobile;
    this.sourceType = this.dialogData.sourceType;
    this.sourceId = this.dialogData.sourceId;
    this.source = ` ${(this.blahService.blah('tendering.addToTendering.from' + this.sourceType))} ${(this.dialogData.sourceIwmId || '').toString()} `;
    this.allowedToCopyUnits = this.dialogData.allowedToCopyUnits;
    this.locale = this.localeService.getLocale();
    this.timeoutInterval = this.timeoutIntervalService.getTimeoutInterval();

    if (this.mobile) {
      const dialogAction = this.dialogData['actionClicked'] as Observable<void>;
      if (dialogAction) {
        dialogAction.subscribe(() => {
          this.addItemsToTendering();
        })
      }
    }
  }

  ngOnInit(): void {
    this.tenderingService.getOwnTenderingsWithNoSentBids()
      .subscribe((result: Array<TenderingAsListItem>) => {
        // sorted here instead of oData query to handle null/empty values correctly
        this.tenderings = <Array<TenderingAsListItem>>result.sort((a, b) => {
          let aVal = (a.Identifier + a.Name).toLowerCase();
          let bVal = (b.Identifier + b.Name).toLowerCase();
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        });
        this.filteredOptions = this.tenderingControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .pipe(
            debounceTime(400),
            startWith<string | TenderingAsListItem>(''),
            map(value => typeof value === 'string' ? value : value.Identifier + value.Name),
            map(name => { return name ? this._filter(name) : this.tenderings.slice() })
          );
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayFn(tendering?: TenderingAsListItem): string | undefined {
    return tendering ? tendering.Identifier + tendering.Name : undefined;
  }

  get validToAdd(): boolean {
    return this.tenderingControl.value !== null && this.tenderingControl.value.Id !== undefined && (this.copyFileAttachments.value || this.copyUnits.value);
  }

  get addingItemsInfoText(): string {
    let textParts = [];
    if (this.copyFileAttachments.value) {
      textParts.push(this.blahService.blah('tendering.addToTendering.attachments'));
    }
    if (this.copyUnits.value) {
      textParts.push(this.blahService.blah('tendering.addToTendering.units'));
    }
    let text = textParts.join(` ${this.blahService.blah('tendering.addToTendering.and')} `);
    return this.locale.language === 'sv' ? text[0].toUpperCase() + text.slice(1) : text;
  }

  public addItemsToTendering() {
    if (!this.validToAdd) {
      return;
    }

    this.addInProgress = true;
    this.cdRef.markForCheck();

    let success = [];
    let timeout = false;

    const tenderingId = this.tenderingControl.value.Id;

    const copyAttachments = this.copyFileAttachments.value;
    const copyUnits = this.allowedToCopyUnits && this.copyUnits.value;

    const subscriptions = this.sourceType === 'Task'
      ? [(copyAttachments ? this.tenderingService.copyFileAttachmentsFromTaskToTendering(tenderingId, this.sourceId) : of(null)).pipe(timeInterval()),
      (copyUnits ? this.tenderingService.copyUnitsFromTask(tenderingId, this.sourceId) : of(null)).pipe(timeInterval())]
      : [(copyAttachments ? this.tenderingService.copyFileAttachmentsFromWorksiteToTendering(tenderingId, this.sourceId) : of(null)).pipe(timeInterval()),
      (copyUnits ? this.tenderingService.copyUnitsFromWorksite(tenderingId, this.sourceId) : of(null)).pipe(timeInterval())];

    forkJoin(subscriptions)
      .pipe(finalize(() => {
        this.dialogRef.close({ success, timeout, tenderingId });
      }))
      .subscribe({
        next: (result: Array<{ value: boolean, interval: number }>) => {
          const failed = [];
          if (copyAttachments) {
            if (result[0].value) {
              success.push('Attachments');
            }
            else if (result[0].interval > this.timeoutInterval) {
              timeout = true;
            }
            else {
              failed.push('Attachments');
            }
          }
          if (copyUnits) {
            if (result[1].value) {
              success.push('Units');
            }
            else if (result[1].interval > this.timeoutInterval) {
              timeout = true;
            }
            else {
              failed.push('Units');
            }
          }
          if (timeout || failed.length > 0) {
            let options = timeout ? { duration: ToastDuration.persistent, actionLabel: 'OK' } : {};
            this.toaster.create(this.blahService.blah(timeout
              ? 'tendering.addToTendering.addTimeouted'
              : (failed.length === 1
                ? `tendering.addToTendering.add${failed[0]}Failed`
                : `tendering.addToTendering.addFailed`)), options);
          }
        },
        error: (res) => {
          this.toaster.create(this.blahService.blah('tendering.addToTendering.addFailed'));
        }
      })
  }

  close(): void {
    this.dialogRef.close();
  }

  private _filter(value: string): TenderingAsListItem[] {
    const filterValue = value.toLowerCase();
    return this.tenderings.filter(option => (option.Identifier + option.Name).toLowerCase().indexOf(filterValue) >= 0);
  }

}
